/**
 * ============================================================================
 * Last Save Indicator Component
 * ============================================================================
 * 
 * Affiche l'heure de la dernière sauvegarde avec mise à jour temps réel
 * 
 * ============================================================================
 */

import { Badge } from '@/components/ui/badge';
import { Check } from 'lucide-react';
import { useEffect, useState } from 'react';

export interface LastSaveIndicatorProps {
  lastSaveTime: Date | string | null;
}

export function LastSaveIndicator({ lastSaveTime }: LastSaveIndicatorProps) {
  const [timeAgo, setTimeAgo] = useState<string>('');

  useEffect(() => {
    if (!lastSaveTime) {
      setTimeAgo('');
      return;
    }

    const updateTimeAgo = () => {
      const now = new Date();
      // Convert to Date if it's a string
      const saveTime = lastSaveTime instanceof Date ? lastSaveTime : new Date(lastSaveTime);
      const diffMs = now.getTime() - saveTime.getTime();
      const diffSeconds = Math.floor(diffMs / 1000);
      const diffMinutes = Math.floor(diffSeconds / 60);
      const diffHours = Math.floor(diffMinutes / 60);

      if (diffSeconds < 10) {
        setTimeAgo('à l\'instant');
      } else if (diffSeconds < 60) {
        setTimeAgo(`il y a ${diffSeconds}s`);
      } else if (diffMinutes < 60) {
        setTimeAgo(`il y a ${diffMinutes}min`);
      } else if (diffHours < 24) {
        setTimeAgo(`il y a ${diffHours}h`);
      } else {
        // Format: HH:MM
        const hours = saveTime.getHours().toString().padStart(2, '0');
        const minutes = saveTime.getMinutes().toString().padStart(2, '0');
        setTimeAgo(`à ${hours}:${minutes}`);
      }
    };

    updateTimeAgo();
    const interval = setInterval(updateTimeAgo, 1000); // Update every second

    return () => clearInterval(interval);
  }, [lastSaveTime]);

  if (!lastSaveTime || !timeAgo) {
    return null;
  }

  return (
    <Badge variant="outline" className="gap-1.5 text-xs font-normal">
      <Check className="h-3 w-3 text-green-600" />
      <span className="text-muted-foreground">Sauvegardé {timeAgo}</span>
    </Badge>
  );
}

